import { useState, useRef, useEffect } from 'react';
import {
    Autocomplete,
    Box,
    CircularProgress,
    InputAdornment,
    Paper,
    Stack,
    TextField,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { MemberIcon } from '@app/member/MemberIcon';
import { MissionIcon } from '@app/mission/MissionIcon';
import { AntennaIcon } from '@app/antenna/AntennaIcon';
import { MemberLightCard } from '@app/member/show/MemberLightCard';
import {
    AnimationLight,
    AntennaLight,
    FormationLight,
    MemberLight,
    MissionLight,
    SupplyLight,
} from '@wwf/sdk';
import { AntennaLightCard } from '@app/antenna/show/AntennaLightCard';
import { MissionLightCard } from '@app/mission/show/MissionLightCard';
import { FormationIcon } from '@app/formation/FormationIcon';
import { FormationLightCard } from '@app/formation/show/FormationLightCard';
import { AnimationIcon } from '@app/animation/AnimationIcon';
import { AnimationLightCard } from '@app/animation/show/AnimationLightCard';
import {
    SearchResultWithUrl,
    useGlobalSearch,
} from '@app/misc/useGlobalSearch';
import { SupplyIcon } from '@app/supply/SupplyIcon';
import { SupplyLightCard } from '@app/supply/show/SupplyLightCard';

export const SearchOmnibox = () => {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);
    const skipSearchRef = useRef(false);

    const { results, isLoading } = useGlobalSearch(searchQuery);

    // Debounce search query (skip when change is from selecting an option)
    useEffect(() => {
        const timer = setTimeout(() => {
            if (skipSearchRef.current) {
                skipSearchRef.current = false;
                setSearchQuery('');
                return;
            }
            // Only search if input has at least 2 characters
            if (inputValue.length >= 2) {
                setSearchQuery(inputValue);
            } else {
                setSearchQuery('');
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [inputValue]);

    // Keyboard shortcut handler
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
                event.preventDefault();
                setOpen(true);
                // Focus the input after the autocomplete opens
                setTimeout(() => {
                    const inputElement =
                        inputRef.current?.querySelector('input');
                    inputElement?.focus();
                }, 100);
            }
            if (event.key === 'Escape') {
                setOpen(false);
                setInputValue('');
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleSelect = (result: SearchResultWithUrl | null) => {
        if (result) {
            navigate(result.url);
            setOpen(false);
            setInputValue('');
        }
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setInputValue('');
    };

    return (
        <Autocomplete<SearchResultWithUrl, false, false, false>
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            inputValue={inputValue}
            onInputChange={(_, newInputValue, reason) => {
                setInputValue(newInputValue);
                // When selecting an option, Autocomplete fills the input with the option label (reason === 'reset').
                // Don't trigger a new server query for that.
                if (reason === 'reset') {
                    skipSearchRef.current = true;
                    setSearchQuery('');
                }
            }}
            onChange={(_, newValue) => handleSelect(newValue)}
            options={results}
            getOptionLabel={option => option.title}
            filterOptions={options => options}
            loading={isLoading}
            loadingText="Searching..."
            noOptionsText={
                searchQuery
                    ? 'No results found'
                    : 'Start typing to search (2 characters minimum)...'
            }
            PaperComponent={({ children, ...other }) => (
                <Paper
                    {...other}
                    sx={{
                        overflow: 'auto',
                        maxWidth: 400,
                        '& .MuiAutocomplete-listbox': {
                            padding: 0,
                            maxHeight: 600,
                            height: 'auto',
                        },
                    }}
                >
                    {children}
                </Paper>
            )}
            renderInput={params => (
                <TextField
                    {...params}
                    ref={inputRef}
                    placeholder="Search members, missions, antennas... (⌘K)"
                    variant="outlined"
                    size="small"
                    sx={{
                        minWidth: 200,
                        maxWidth: 400,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'background.paper',
                        },
                    }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <>
                                {isLoading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => {
                return (
                    <Stack
                        direction="row"
                        alignItems="flex-start"
                        component="li"
                        {...props}
                        key={option.id}
                        gap={1}
                        sx={{ minWidth: 0, width: '100%' }}
                    >
                        <Box sx={{ flexShrink: 0, pt: 0.25 }}>
                            {getTypeIcon(option.resultType)}
                        </Box>
                        <Box
                            sx={{
                                minWidth: 0,
                                flex: 1,
                                '& .MuiTypography-root': {
                                    whiteSpace: 'normal',
                                    overflowWrap: 'anywhere',
                                },
                            }}
                        >
                            {getTypeComponent(option)}
                        </Box>
                    </Stack>
                );
            }}
        />
    );
};

const getTypeIcon = (searchType: SearchResultWithUrl['resultType']) => {
    switch (searchType) {
        case 'member':
            return <MemberIcon />;
        case 'mission':
            return <MissionIcon />;
        case 'antenna':
            return <AntennaIcon />;
        case 'formation':
            return <FormationIcon />;
        case 'animation':
            return <AnimationIcon />;
        case 'supply':
            return <SupplyIcon />;
        default:
            searchType satisfies never;
            return <Search fontSize="small" />;
    }
};

const getTypeComponent = (option: SearchResultWithUrl) => {
    switch (option.resultType) {
        case 'member':
            return (
                <MemberLightCard record={option.record as MemberLight} noLink />
            );
        case 'mission':
            return (
                <MissionLightCard
                    record={option.record as MissionLight}
                    noLink
                    twoLines
                />
            );
        case 'antenna':
            return (
                <AntennaLightCard
                    record={option.record as AntennaLight}
                    noLink
                />
            );
        case 'formation':
            return (
                <FormationLightCard
                    record={option.record as FormationLight}
                    noLink
                />
            );
        case 'animation':
            return (
                <AnimationLightCard
                    record={option.record as AnimationLight}
                    noLink
                />
            );
        case 'supply':
            return (
                <SupplyLightCard record={option.record as SupplyLight} noLink />
            );
        default:
            option.resultType satisfies never;
            return null;
    }
};
